import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { Box, type SxProps, type Theme } from '@mui/material';

type Props = {
  content: string;
  /** Optional MUI sx merged onto the wrapper (typography defaults for assistant prose). */
  sx?: SxProps<Theme>;
};

const proseSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  lineHeight: 1.55,
  '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
  '& h1': { fontSize: '1.25rem', fontWeight: 700, mt: 1.5, mb: 1 },
  '& h2': { fontSize: '1.1rem', fontWeight: 700, mt: 1.25, mb: 0.75 },
  '& h3, & h4, & h5, & h6': { fontSize: '1rem', fontWeight: 600, mt: 1, mb: 0.5 },
  '& ul, & ol': { pl: 2.25, my: 0.75, '&:first-of-type': { mt: 0 } },
  '& li': { mb: 0.35 },
  '& strong, & b': { fontWeight: 700 },
  '& em, & i': { fontStyle: 'italic' },
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: '0.8125rem',
    bgcolor: 'action.hover',
    px: 0.5,
    py: 0.125,
    borderRadius: 0.5,
  },
  '& pre': {
    m: 0,
    my: 1,
    p: 1.25,
    bgcolor: 'grey.100',
    borderRadius: 1,
    overflow: 'auto',
    maxWidth: '100%',
    '& code': { bgcolor: 'transparent', p: 0, fontSize: '0.75rem' },
  },
  '& blockquote': {
    m: 0,
    my: 1,
    pl: 1.5,
    borderLeft: 3,
    borderColor: 'divider',
    color: 'text.secondary',
  },
  '& hr': { my: 1.5, border: 0, borderTop: 1, borderColor: 'divider' },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1, fontSize: '0.8125rem' },
  '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
  '& th': { bgcolor: 'action.hover', fontWeight: 600 },
  '& a': { color: 'primary.main', wordBreak: 'break-word' },
};

/**
 * Renders assistant / API prose as GitHub-flavored Markdown with sanitization
 * (no raw HTML execution from model output). Matches test-frontend behavior.
 */
export function AssistantMarkdown({ content, sx }: Props) {
  const src = content ?? '';
  return (
    <Box className="assistant-md" sx={(sx ? [proseSx, sx] : proseSx) as SxProps<Theme>}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
        {src}
      </ReactMarkdown>
    </Box>
  );
}
